import {
  DEFAULT_COLUMNS,
  makeColumnKey,
} from "@/constants/operating-points-constants";
import {
  dedupeOperatingPointsTabsById,
  disambiguateDuplicateOperatingPointsTabLabels,
  getCustomOperatingPointsTabId,
} from "@/lib/operating-points-tab-utils.js";

/**
 * @typedef {Object} OperatingPointsSavedColumnRow
 * @property {string} [labelName]
 * @property {string} [label]
 * @property {string} [tagName]
 * @property {string} [tag]
 * @property {string} [unit]
 * @property {number|string} [universalDescriptorId]
 */

/**
 * @param {OperatingPointsSavedColumnRow} col
 * @returns {{ tagName: string, udId: number, hasUd: boolean }}
 */
export function getSavedColumnTagAndUniversalDescriptor(col) {
  const tagName = col.tagName || col.tag || "";
  const udRaw = col.universalDescriptorId;
  const udId =
    udRaw != null && udRaw !== "" ? Number.parseInt(String(udRaw), 10) : NaN;
  return {
    tagName,
    udId,
    hasUd: Number.isFinite(udId),
  };
}

/**
 * @param {Object|Array|null|undefined} response
 * @returns {Array<Object>|null}
 */
export function getUserDisplaySettingsListFromApiResponse(response) {
  if (Array.isArray(response)) return response;
  if (
    response?.UserDisplaySettings
    && Array.isArray(response.UserDisplaySettings)
  ) {
    return response.UserDisplaySettings;
  }
  if (response?.data?.UserDisplaySettings) {
    return response.data.UserDisplaySettings;
  }
  return null;
}

/**
 * @param {Object} setting
 * @returns {number|null}
 */
function normalizeSettingsRowId(setting) {
  const settingId =
    setting.AppUserDisplaySettingsId
    || setting.appUserDisplaySettingsId
    || setting.id;
  if (typeof settingId === "number" && settingId > 0 && isFinite(settingId)) {
    return settingId;
  }
  if (typeof settingId === "string" && /^\d+$/.test(settingId)) {
    const parsed = parseInt(settingId, 10);
    return !isNaN(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
}

/**
 * Build tabs and column state from the API's full Operating Points settings list.
 *
 * @param {Array<Object>} userDisplaySettings
 * @param {(input: { tagName?: string, universalDescriptorId?: number|null }) => string} getColumnUnit
 * @returns {{
 *   tabs: Array<{ id: string, label: string }>,
 *   customColumns: Object.<string, Array>,
 *   savedColumns: Object.<string, Array>,
 *   viewNameToBackendId: Object.<string, number>
 * }}
 */
export function buildOperatingPointsViewsStateFromUserDisplaySettings(
  userDisplaySettings,
  getColumnUnit
) {
  const builtTabs = [];
  const customColumns = {};
  const viewNameToBackendId = {};
  const seenIds = new Set();
  const defaultColumnTagNames = new Set(
    DEFAULT_COLUMNS.map((col) => col.tagName)
  );

  for (const setting of userDisplaySettings) {
    const normalizedId = normalizeSettingsRowId(setting);
    if (!normalizedId || seenIds.has(normalizedId)) continue;

    if (!setting.UserDisplaySettingsName) continue;

    const dataString = setting.Data || setting.data || "[]";
    let columnsData;
    try {
      columnsData = JSON.parse(dataString);
    } catch {
      continue;
    }
    if (!Array.isArray(columnsData)) continue;

    seenIds.add(normalizedId);

    const tabId = getCustomOperatingPointsTabId(normalizedId);
    viewNameToBackendId[tabId] = normalizedId;

    builtTabs.push({
      id: tabId,
      label: setting.UserDisplaySettingsName,
    });

    customColumns[tabId] = columnsData
      .filter((col) => {
        const { tagName, hasUd } = getSavedColumnTagAndUniversalDescriptor(col);
        if (hasUd && !tagName) return true;
        return !defaultColumnTagNames.has(tagName);
      })
      .map((col) => {
        const { tagName, udId, hasUd } =
          getSavedColumnTagAndUniversalDescriptor(col);

        const unit =
          getColumnUnit({
            tagName: hasUd ? "" : tagName,
            universalDescriptorId: hasUd ? udId : null,
          })
          || col.unit
          || "";

        return {
          key: hasUd
            ? makeColumnKey(col.labelName || col.label || "", "", udId)
            : makeColumnKey(col.labelName || col.label || "", tagName),
          labelName: col.labelName || col.label || "",
          unit,
          tagName: hasUd ? "" : tagName,
          universalDescriptorId: hasUd ? udId : undefined,
          _tabId: tabId,
        };
      });
  }

  const tabs = dedupeOperatingPointsTabsById(
    disambiguateDuplicateOperatingPointsTabLabels(builtTabs)
  );

  const customColumnsWithTabIds = Object.fromEntries(
    Object.entries(customColumns).map(([tabId, cols]) => [
      tabId,
      (cols || []).map((c) => ({ ...c, _tabId: c._tabId || tabId })),
    ])
  );

  const savedColumns = Object.fromEntries(
    Object.entries(customColumnsWithTabIds).map(([tabId, cols]) => [
      tabId,
      JSON.parse(JSON.stringify(cols)),
    ])
  );

  return {
    tabs,
    customColumns: customColumnsWithTabIds,
    savedColumns,
    viewNameToBackendId,
  };
}

/**
 * Tab id for a view name when duplicates exist (highest AppUserDisplaySettingsId wins).
 *
 * @param {Array<Object>} userDisplaySettings
 * @param {string} viewLabel
 * @returns {string|null}
 */
export function findOperatingPointsTabIdForViewName(
  userDisplaySettings,
  viewLabel
) {
  let maxId = null;
  for (const setting of userDisplaySettings) {
    if (setting.UserDisplaySettingsName !== viewLabel) continue;
    const id = normalizeSettingsRowId(setting);
    if (id != null && (maxId == null || id > maxId)) {
      maxId = id;
    }
  }
  return maxId != null ? getCustomOperatingPointsTabId(maxId) : null;
}
